const resolveMediaUrl = (candidate) => {
  const raw = candidate && typeof candidate === 'object'
    ? (candidate.Url || candidate.url || candidate.ImageUrl || candidate.src || '')
    : candidate;
  if (typeof raw !== 'string') return '';
  const url = raw.trim();
  if (!url || /^(javascript|data|vbscript|file):/i.test(url)) return '';
  return /^https?:\/\//i.test(url) || /^\/(?!\/)/.test(url)
    || /^[\w.-]+\.(jpg|jpeg|png|webp|gif)(\?.*)?$/i.test(url)
    ? url : '';
};

/** Real supplier photos, not synthetic variants or media placeholders. */
export const resolvePreviewProductImages = (product = {}) => {
  const variants = Array.isArray(product.Variants) ? product.Variants : [];
  const candidates = [
    product.ImageUrl, product.imageUrl, product.image,
    ...(Array.isArray(product.Images) ? product.Images : []),
    ...(Array.isArray(product.images) ? product.images : []),
    ...variants.flatMap((variant) => [variant?.ImageUrl,
      ...(Array.isArray(variant?.Images) ? variant.Images : [])]),
  ];
  return Array.from(new Set(candidates.map(resolveMediaUrl).filter(Boolean))).slice(0, 16);
};

export const resolvePreviewProductImage = (product = {}) => (
  resolvePreviewProductImages(product)[0] || ''
);

export const resolvePreviewProductName = (product = {}) => (
  String(product.Name || product.name || product.title || 'Untitled product').trim()
);

const positivePrice = (value) => {
  const raw = value && typeof value === 'object' ? value.Price : value;
  if (raw == null || raw === '' || typeof raw === 'boolean') return null;
  const amount = Number(raw);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

const currencyCode = (value) => {
  const code = String(value == null ? '' : value).trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : '';
};

/** Price rows' explicit USD currency outranks historic Currencies=['CAD']. */
export const resolvePreviewProductPrice = (product = {}) => {
  const currencies = product.Currencies || product.currencies || [];
  const fallback = currencyCode(product.CurrencyCode || product.currencyCode || product.currency)
    || currencyCode(Array.isArray(currencies) ? currencies[0] : currencies);
  const candidates = [product.LowestPrice, product.lowestPrice, product.Price,
    product.price, product.ClientPrice, product.clientPrice, product.Retail, product.retail];
  for (const candidate of candidates) {
    const amount = positivePrice(candidate);
    if (amount === null || (candidate && typeof candidate === 'object' && candidate.IsQUR === true)) continue;
    const explicit = candidate && typeof candidate === 'object'
      ? currencyCode(candidate.CurrencyCode || candidate.Currency) : '';
    return { amount, currency: explicit || fallback };
  }
  return null;
};

export const resolvePreviewProductSupplier = (product = {}) => {
  const supplier = product.SupplierName || product.supplierName || product.Supplier || product.supplier;
  if (supplier && typeof supplier === 'object') {
    return String(supplier.Name || supplier.name || '').trim();
  }
  return String(supplier || '').trim();
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const resolveProductId = (product = {}) => String(
  product.ProductId ?? product.productId ?? product.Id ?? product.id ?? ''
).trim();

const resolveProductSource = (product = {}) => {
  const source = normalizeText(product.Source || product.source || product.LookupType || product.lookupType);
  return source === 'asi' ? 'promoworld' : source;
};

const resolveProductSupplier = resolvePreviewProductSupplier;

const clampColumns = (value, fallback, maximum) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(1, Math.min(maximum, Math.round(parsed))) : fallback;
};

export const resolveProductGridColumns = (props = {}) => ({
  desktop: clampColumns(props.columns_desktop ?? props.columns, 4, 6),
  tablet: clampColumns(props.columns_tablet, 3, 4),
  mobile: clampColumns(props.columns_mobile, 2, 2),
});

export const filterPreviewProducts = ({ props = {}, products = [] }) => {
  let result = Array.isArray(products) ? [...products] : [];
  const sourceRaw = normalizeText(props.query_source || props.source || 'all');
  const source = sourceRaw === 'asi' ? 'promoworld' : sourceRaw;
  const search = normalizeText(props.query_search);
  const supplier = normalizeText(props.query_supplier);
  const ids = String(props.query_product_ids || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
  const sort = String(props.query_sort || 'default').trim();

  if (source && source !== 'all') {
    result = result.filter((product) => resolveProductSource(product) === source);
  }

  if (supplier) {
    result = result.filter((product) => normalizeText(resolveProductSupplier(product)).includes(supplier));
  }

  if (search) {
    result = result.filter((product) => {
      const haystack = [
        resolvePreviewProductName(product),
        resolveProductSupplier(product),
        resolveProductId(product),
        product.SKU || product.sku || '',
      ].map(normalizeText).join(' ');
      return haystack.includes(search);
    });
  }

  if (ids.length) {
    const order = new Map(ids.map((id, index) => [id, index]));
    result = result
      .filter((product) => order.has(resolveProductId(product)))
      .sort((a, b) => order.get(resolveProductId(a)) - order.get(resolveProductId(b)));
  }

  if (sort === 'name_asc' || sort === 'name_desc') {
    const direction = sort === 'name_desc' ? -1 : 1;
    result.sort((a, b) => direction * resolvePreviewProductName(a).localeCompare(resolvePreviewProductName(b)));
  }

  return result;
};

const previewDescription = (product = {}) => {
  const value = String(product.ShortDescription || product.shortDescription
    || product.Description || product.description || '');
  const clean = value.replace(/<[^>]*>/g, ' ')
    .replace(/\[[^\]]+\]/g, ' ').replace(/(?:^|\s)[*•]+\s*/g, ' ')
    .replace(/\s+/g, ' ').trim();
  const words = clean.split(/\s+/).filter(Boolean);
  return words.length > 17 ? words.slice(0, 17).join(' ') + '…' : clean;
};

const knownSwatches = {
  white: '#ffffff', black: '#222b38', red: '#dc2626',
  pink: '#f472b6', 'neon pink': '#ff46a8',
  orange: '#ed8a36', 'neon orange': '#ff8b21',
  'sky blue': '#71b7ee', blue: '#347ec9',
  navy: '#213a68', 'navy blue': '#213a68',
  'neon lime': '#b5ed3b', green: '#3a9a5f',
  'spring green': '#79c581', 'canary yellow': '#ffe359',
  'ultra yellow': '#ffdf27', yellow: '#f6d64a',
  grey: '#a0aab5', gray: '#a0aab5', purple: '#815db5',
};

const previewColors = (product = {}) => {
  const colors = new Map();
  const source = [
    ...(Array.isArray(product.Colors) ? product.Colors : []),
    ...(Array.isArray(product.Attributes?.Colors?.Values) ? product.Attributes.Colors.Values : []),
    ...(Array.isArray(product.Variants) ? product.Variants.map((variant) =>
      ({ Name: variant?.Color, ColorHex: variant?.ColorHex })) : []),
  ];
  source.forEach((candidate) => {
    const name = String(candidate && typeof candidate === 'object'
      ? (candidate.Name || candidate.name || '') : candidate || '').trim();
    if (!name || colors.has(name.toLowerCase())) return;
    const rawHex = candidate && typeof candidate === 'object'
      ? (candidate.ColorHex || candidate.Hex || '') : '';
    const verified = /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i.test(String(rawHex)) ? String(rawHex) : '';
    colors.set(name.toLowerCase(), { name, hex: verified || knownSwatches[name.toLowerCase()] || '' });
  });
  return Array.from(colors.values());
};

const previewFacts = (product = {}) => {
  const facts = [];
  const minimum = [product.MinimumOrderQuantity, product.MinOrderQuantity,
    product.MinOrderQty, product.MinimumQuantity, product.CustomData?.MinimumOrderQuantity]
    .find((value) => value != null && value !== '' && Number.isInteger(Number(value)) && Number(value) > 0);
  if (minimum != null) facts.push('MOQ ' + Number(minimum).toLocaleString('en-US'));
  const declaredTime = String(product.ProductionTime || product.LeadTime || '');
  const production = declaredTime.match(/^(\d{1,3})\s*(business|working)\s*days?$/i)
    || String(product.Description || '').match(/Production\s+Time\s*:\s*(\d{1,3})\s*(business|working)\s*days?\b/i);
  if (production) facts.push(production[1] + ' business days');
  const optionCount = Math.max(0, Number(product.VariantCount || 0),
    Array.isArray(product.Variants) ? product.Variants.length : 0);
  if (optionCount > 1) facts.push(optionCount + ' options');
  return facts.slice(0, 2);
};

export const buildPreviewProductGridHtml = ({ props = {}, products = [], escapeHtml, showPrices = true }) => {
  const requestedPageSize = Number(props.page_size ?? 24);
  const pageSize = Number.isFinite(requestedPageSize) && requestedPageSize >= 0 ? requestedPageSize : 24;
  const columns = resolveProductGridColumns(props);
  const availableProducts = Array.isArray(products) ? products : [];
  const visibleProducts = filterPreviewProducts({ props, products: availableProducts }).slice(0, pageSize);
  const gridStyle = `--ps-product-cols-desktop:${columns.desktop};--ps-product-cols-tablet:${columns.tablet};--ps-product-cols-mobile:${columns.mobile};`;

  if (!visibleProducts.length) {
    const message = availableProducts.length
      ? 'No products match this grid query.'
      : 'No products available for preview.';
    return `<div class="ps-product-grid" style="${gridStyle}"><div class="ps-product-grid__empty">${message}</div></div>`;
  }

  const cards = visibleProducts.map((product) => {
    const name = resolvePreviewProductName(product);
    const photos = resolvePreviewProductImages(product);
    const supplierName = resolveProductSupplier(product);
    const price = resolvePreviewProductPrice(product);
    const description = previewDescription(product);
    const facts = previewFacts(product);
    const colors = previewColors(product);
    const visibleSwatches = colors.filter((color) => Boolean(color.hex)).slice(0, 4);
    const remaining = Math.max(0, colors.length - visibleSwatches.length);
    const provider = String(product.Source || product.source || 'Catalogue').trim();
    const source = [provider, supplierName].filter(Boolean).join(' · ');

    const image = photos.length
      ? '<img class="ps-product-card__image" src="' + escapeHtml(photos[0]) + '" alt="' + escapeHtml(name) + '" loading="lazy" />'
      : '<span class="ps-product-card__image-empty">Image coming soon</span>';
    const media = '<div class="ps-product-card__media">' + image
      + (photos.length > 1 ? '<span class="ps-product-card__photos">' + photos.length + ' photos</span>' : '')
      + '</div>';

    const descriptionHtml = description
      ? '<p class="ps-product-card__description">' + escapeHtml(description) + '</p>' : '';
    const factsHtml = facts.length
      ? '<div class="ps-product-card__facts">'
        + facts.map((value) => '<span>' + escapeHtml(value) + '</span>').join('') + '</div>'
      : '';
    let priceHtml = '';
    if (showPrices) {
      if (price) {
        priceHtml = '<div class="ps-product-card__price"><span class="ps-product-card__price-prefix">From</span>'
          + '<strong>' + escapeHtml('$' + price.amount.toFixed(2) + (price.currency ? ' ' + price.currency : ''))
          + '</strong><small>Supplier catalogue price</small></div>';
      } else {
        priceHtml = '<div class="ps-product-card__price"><span class="ps-product-card__price-request">Price on request</span></div>';
      }
    }
    const swatchesHtml = colors.length
      ? '<div class="ps-product-card__colors">'
        + visibleSwatches.map((color) =>
          '<span class="ps-product-card__swatch" title="' + escapeHtml(color.name)
          + '" style="--ps-swatch:' + color.hex + '"></span>').join('')
        + (remaining ? '<span class="ps-product-card__colors-rest">+' + remaining + '</span>' : '')
        + (!visibleSwatches.length ? '<span class="ps-product-card__colors-rest">' + colors.length + ' colours</span>' : '')
        + '</div>'
      : '';

    return '<article class="ps-product-card">' + media
      + '<div class="ps-product-card__details">'
      + '<div class="ps-product-card__source">' + escapeHtml(source) + '</div>'
      + '<div class="ps-product-card__name">' + escapeHtml(name) + '</div>'
      + descriptionHtml + factsHtml + priceHtml + swatchesHtml
      + '<span class="ps-product-card__action">View product <span aria-hidden="true">→</span></span>'
      + '</div></article>';
  }).join('');

  return `<div class="ps-product-grid" style="${gridStyle}">${cards}</div>`;
};


const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null || value === '') return [];
  if (typeof value === 'string') {
    return value.split(/[|,;]/).map((part) => part.trim()).filter(Boolean);
  }
  return [value];
};

const taxonomyFieldCandidates = {
  brand: ['Brand', 'brand', 'BrandName', 'brandName'],
  theme: ['Theme', 'theme', 'ThemeName', 'themeName', 'Themes', 'themes'],
  product_category: ['Category', 'category', 'CategoryName', 'categoryName', 'Categories', 'categories'],
};

const readNestedCandidates = (product = {}, keys = []) => {
  const values = [];
  keys.forEach((key) => {
    values.push(...asArray(product?.[key]));
    values.push(...asArray(product?.CustomData?.[key]));
    values.push(...asArray(product?.customData?.[key]));
  });
  return values.map((value) => String(value || '').trim()).filter(Boolean);
};

export const buildPreviewTaxonomyItems = ({ taxonomy = 'product_category', products = [], merchandising = null, limit = 8 } = {}) => {
  const merchandisingKey = taxonomy === 'brand' ? 'brands' : taxonomy === 'theme' ? 'themes' : 'categories';
  const merchandisingItems = Array.isArray(merchandising?.[merchandisingKey]) ? merchandising[merchandisingKey] : [];
  if (merchandisingItems.length) {
    return merchandisingItems
      .slice(0, Math.max(1, Number(limit) || 8))
      .map((item) => ({
        label: item?.name || item?.label || 'Collection',
        image: item?.image || '',
        count: Number(item?.productCount || item?.count || 0),
      }));
  }
  const keys = taxonomyFieldCandidates[taxonomy] || taxonomyFieldCandidates.product_category;
  const buckets = new Map();

  (Array.isArray(products) ? products : []).forEach((product) => {
    const image = resolvePreviewProductImage(product);
    const labels = readNestedCandidates(product, keys);
    labels.forEach((label) => {
      const normalized = normalizeText(label);
      if (!normalized) return;
      if (!buckets.has(normalized)) {
        buckets.set(normalized, {
          label,
          image,
          count: 0,
        });
      }
      const item = buckets.get(normalized);
      item.count += 1;
      if (!item.image && image) item.image = image;
    });
  });

  return Array.from(buckets.values())
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, Math.max(1, Number(limit) || 8));
};
